import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { BrowserRouter, useNavigate, Routes, Route, useLocation } from 'react-router-dom';
import damerau from 'damerau-levenshtein';
import { useAuthStore } from './store/authStore';
import { useMultipleLoadingStates } from './hooks/useLoadingState';
import LoadingMessage from './components/LoadingMessage';

// Firebase
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { collection, onSnapshot, addDoc, doc, setDoc, getDocs, deleteDoc, updateDoc, query, writeBatch, where, getDoc, orderBy, serverTimestamp } from 'firebase/firestore';
import { db, auth } from './firebaseConfig';
import { getTenantId, SUPER_ADMIN_EMAIL } from './utils/tenantUtils';
import { formatLocalDateToISO, getConnectWeekdayIndex } from './utils/dateUtils';

// Componentes
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import DecisionFormPage from './pages/DecisionFormPage';
import LoadingSpinner from './components/LoadingSpinner';
import AppRouter from './AppRouter';
import Modal from './components/Modal';
import ConfirmationModal from './components/ConfirmationModal';
import MemberForm from './components/MemberForm';
import ConnectForm from './components/ConnectForm';
import CourseForm from './components/CourseForm';
import CourseTemplateForm from './components/CourseTemplateForm';
import ManageCourseModal from './components/ManageCourseModal';
import ConnectReportModal from './components/ConnectReportModal';
import ConnectFullReportModal from './components/ConnectFullReportModal';
import IndividualTrackModal from './components/IndividualTrackModal';
import DuplicateMemberModal from './components/DuplicateMemberModal';
import ConnectTrackPage from './pages/ConnectTrackPage';
import MemberDetailsModal from './components/MemberDetailsModal';

const KidsApp = React.lazy(() => import('./kids/KidsApp'));

// Admin é determinado pelo campo isAdmin do membro OU por ser o Super Admin
const isAdminEmail = (email) => {
    if (!email) return false;
    const lowerEmail = email.toLowerCase();
    return lowerEmail === SUPER_ADMIN_EMAIL;
};

// (As funções de utilidade `calculateFinalGradeForStudent`, `getStudentStatusInfo`, `areNamesSimilar` continuam as mesmas aqui...)
// --- FUNÇÕES DE UTILIDADE (HELPERS) ---
const calculateFinalGradeForStudent = (student, course) => {
    const scores = student.scores;
    const { assessment } = course;
    if (!scores || !assessment) return 0;
    let total = 0;
    total += (scores.tests || []).reduce((sum, score) => sum + (Number(score) || 0), 0);
    total += (scores.assignments || []).reduce((sum, score) => sum + (Number(score) || 0), 0);
    total += ((scores.activities || []).filter(done => done).length) * (assessment?.activities?.value || 0);
    return parseFloat(total.toFixed(2));
};

const getStudentStatusInfo = (student, course, attendanceRecords) => {
    if (!attendanceRecords || !course || !student) return 'Pendente';
    // Considerar somente dias válidos (não ignorados e não "sem aula")
    const consideredRecords = attendanceRecords.filter(r => !r.ignoreAttendance && !r.noClass);
    let presentCount = 0;
    consideredRecords.forEach(record => {
        if (record.statuses && (record.statuses[student.id] === 'presente' || record.statuses[student.id] === 'justificado')) {
            presentCount++;
        }
    });
    const attendancePercentage = consideredRecords.length > 0 ? Math.round((presentCount / consideredRecords.length) * 100) : 0;

    const finalGrade = calculateFinalGradeForStudent(student, course);

    const { passingCriteria } = course;
    if (!passingCriteria) return 'Cursando';
    if (attendancePercentage < passingCriteria.minAttendance) return 'Reprovado por Falta';
    if (finalGrade < passingCriteria.minGrade) return 'Reprovado por Nota';

    return 'Aprovado';
};

const areNamesSimilar = (nameA, nameB, threshold) => {
    const normalize = (str) => str.toLowerCase().trim().replace(/\s+/g, ' ');

    const tokensA = normalize(nameA).split(' ');
    const tokensB = normalize(nameB).split(' ');

    if (tokensA.length === 0 || tokensB.length === 0) {
        return false;
    }
    const firstNameA = tokensA[0];
    const firstNameB = tokensB[0];
    if (damerau(firstNameA, firstNameB).similarity < threshold) {
        return false;
    }
    if (tokensA.length === 1 && tokensB.length === 1) {
        return true;
    }
    const surnamesA = tokensA.slice(1);
    const surnamesB = tokensB.slice(1);
    if (surnamesA.length === 0 || surnamesB.length === 0) {
        return true;
    }
    for (const surnameA of surnamesA) {
        for (const surnameB of surnamesB) {
            if (damerau(surnameA, surnameB).similarity >= threshold) {
                return true;
            }
        }
    }
    return false;
};

function AppContent() {
    const { user, isAdmin, currentUserData, setAuthData, clearAuthData, impersonatedUser, clearImpersonation, tenantData } = useAuthStore();
    const [isLoadingAuth, setIsLoadingAuth] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const isKidsRoute = location.pathname.startsWith('/kids');
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [allMembers, setAllMembers] = useState([]);
    const [allConnects, setAllConnects] = useState([]);
    const [allCourses, setAllCourses] = useState([]);
    const [allCourseTemplates, setAllCourseTemplates] = useState([]);
    const [allCertificateTemplates, setAllCertificateTemplates] = useState([]);
    const [allConnectReports, setAllConnectReports] = useState([]);
    const [allDecisions, setAllDecisions] = useState([]);
    const [allSimpleMembers, setAllSimpleMembers] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [connectionError, setConnectionError] = useState(null);
    const [operationStatus, setOperationStatus] = useState({ type: null, message: null });

    // Loading states para operações específicas
    const loadingStates = useMultipleLoadingStates([
        'saveMember', 'saveConnect', 'saveCourse', 'saveCourseTemplate',
        'saveReport', 'deleteMember', 'deleteConnect', 'deleteCourse',
        'reactivateMember', 'saveAttendance', 'saveSimpleMember', 'deleteSimpleMember'
    ]);
    const [isMemberModalOpen, setMemberModalOpen] = useState(false);
    const [isConnectModalOpen, setConnectModalOpen] = useState(false);
    const [isCourseModalOpen, setCourseModalOpen] = useState(false);
    const [isCourseTemplateModalOpen, setCourseTemplateModalOpen] = useState(false);
    const [isManageCourseModalOpen, setManageCourseModalOpen] = useState(false);
    const [isReportModalOpen, setReportModalOpen] = useState(false);
    const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
    const [isConnectFullReportModalOpen, setConnectFullReportModalOpen] = useState(false);
    const [isLeadershipTrackModalOpen, setLeadershipTrackModalOpen] = useState(false);

    const [isDuplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [existingMemberForCheck, setExistingMemberForCheck] = useState(null);
    const [newMemberForCheck, setNewMemberForCheck] = useState(null);
    const [editingMember, setEditingMember] = useState(null);
    const [editingConnect, setEditingConnect] = useState(null);
    const [editingCourse, setEditingCourse] = useState(null);
    const [editingCourseTemplate, setEditingCourseTemplate] = useState(null);
    const [managingCourse, setManagingCourse] = useState(null);
    const [reportingConnect, setReportingConnect] = useState(null);
    const [generatingReportForConnect, setGeneratingReportForConnect] = useState(null);
    const [viewingMember, setViewingMember] = useState(null);

    const [membersWithCourses, setMembersWithCourses] = useState([]);
    const [completedCourses, setCompletedCourses] = useState([]);
    const [deleteAction, setDeleteAction] = useState(null);
    const [memberConnectHistoryDetails, setMemberConnectHistoryDetails] = useState([]);

    // --- Efeitos ---

    // CORRIGIDO: useEffect 1 - Apenas para autenticação
    useEffect(() => {
        const authUnsub = onAuthStateChanged(auth, (currentUser) => {
            setAuthData({
                user: currentUser,
                isAdmin: false, // Será determinado quando os dados do membro forem carregados
                currentUserData: null // Será preenchido pelo próximo useEffect
            });
            setIsLoadingAuth(false);
        });
        return () => authUnsub();
    }, [setAuthData]);

    // useEffect para escutar alterações no tenant ativo em tempo real
    // Ignorado no módulo Kids e para usuários não autenticados (evita erro de permissão)
    useEffect(() => {
        if (isKidsRoute || !user) return;
        const tenantId = getTenantId();
        if (!tenantId) return;

        let qUnsub = null;
        const tenantRef = doc(db, 'tenants', tenantId);
        const mainUnsub = onSnapshot(
            tenantRef,
            (docSnap) => {
                if (docSnap.exists()) {
                    const data = { id: docSnap.id, ...docSnap.data() };
                    useAuthStore.getState().setTenant(tenantId, data);
                } else {
                    // Fallback: busca por campo id (para tenants criados com addDoc/autoId)
                    const q = query(collection(db, 'tenants'), where('id', '==', tenantId));
                    if (!qUnsub) {
                        qUnsub = onSnapshot(q, (qSnap) => {
                            if (!qSnap.empty) {
                                const d = qSnap.docs[0];
                                const data = { docId: d.id, ...d.data() };
                                useAuthStore.getState().setTenant(tenantId, data);
                            }
                        });
                    }
                }
            },
            (err) => {
                console.warn('Erro ao escutar tenant:', err);
            }
        );
        return () => {
            mainUnsub();
            if (qUnsub) qUnsub();
        };
    }, [user, isKidsRoute]);

    // CORRIGIDO: useEffect 2 - Para buscar todos os membros e encontrar o dado do usuário logado
    useEffect(() => {
        if (!user || isKidsRoute) {
            setAllMembers([]); // Usa dados de exemplo se não houver usuário ou em rota Kids
            return;
        }
        const membersUnsub = onSnapshot(
            collection(db, `artifacts/${getTenantId()}/public/data/members`),
            async (snapshot) => {
                const membersList = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

                const finalMembersList = membersList;
                setAllMembers(finalMembersList);

                // Dados do tenant atual e usuário logado
                const currentTenantData = useAuthStore.getState().tenantData;
                const tenantAdminEmail = currentTenantData?.adminEmail?.toLowerCase()?.trim() || null;
                const userEmail = user.email?.toLowerCase()?.trim() || '';

                // Encontra e atualiza os dados do usuário logado no store
                const realMemberData = finalMembersList.find(m => m.email?.toLowerCase()?.trim() === userEmail);
                const isTenantAdmin = !!(tenantAdminEmail && tenantAdminEmail === userEmail);
                const realIsAdmin = isAdminEmail(user.email) || isTenantAdmin || (realMemberData?.isAdmin === true);

                // Se o membro existe na igreja mas a flag isAdmin ainda não está true e ele é o admin
                if (realMemberData && isTenantAdmin && !realMemberData.isAdmin) {
                    try {
                        await updateDoc(doc(db, `artifacts/${getTenantId()}/public/data/members`, realMemberData.id), {
                            isAdmin: true
                        });
                    } catch (err) {
                        console.warn('Erro ao atualizar flag isAdmin do membro:', err);
                    }
                }

                // Fallback de dados do usuário para Administradores que ainda não têm registro de membro
                const fallbackUserData = realIsAdmin ? {
                    id: realMemberData?.id || 'admin-user',
                    email: user.email,
                    name: user.displayName || user.email?.split('@')[0] || 'Administrador',
                    isAdmin: true,
                    role: 'Administrador',
                    status: 'Ativo'
                } : null;

                if (impersonatedUser) {
                    setAuthData({ user, isAdmin: (impersonatedUser.isAdmin === true), currentUserData: impersonatedUser });
                } else {
                    setAuthData({ user, isAdmin: realIsAdmin, currentUserData: realMemberData || fallbackUserData });
                }
            },
            (error) => {
                console.error('Erro ao carregar membros:', error);
                setConnectionError('Erro de conexão: Não foi possível carregar os dados dos membros.');
                setAllMembers([]);
                const currentTenantData = useAuthStore.getState().tenantData;
                const tenantAdminEmail = currentTenantData?.adminEmail?.toLowerCase()?.trim() || null;
                const userEmail = user.email?.toLowerCase()?.trim() || '';
                const userIsAdmin = isAdminEmail(user.email) || (tenantAdminEmail && tenantAdminEmail === userEmail);
                setAuthData({
                    user,
                    isAdmin: userIsAdmin,
                    currentUserData: userIsAdmin ? { email: user.email, name: 'Administrador', isAdmin: true } : null
                });
            }
        );
        return () => membersUnsub();
    }, [user, setAuthData, impersonatedUser]);

    // useEffect extra: Recalcula isAdmin quando tenantData muda (resolve race condition)
    // O tenantData pode carregar DEPOIS do snapshot de membros, então precisamos recalcular.
    useEffect(() => {
        if (!user || !tenantData?.adminEmail) return;
        const tenantAdminEmail = tenantData.adminEmail.toLowerCase().trim();
        const userEmail = user.email?.toLowerCase()?.trim() || '';
        const isTenantAdmin = tenantAdminEmail === userEmail;
        
        if (isTenantAdmin && !isAdmin) {
            // O usuário É o admin da igreja mas isAdmin ainda está false — recalcula
            const realMemberData = allMembers.find(m => m.email?.toLowerCase()?.trim() === userEmail);
            const fallbackUserData = {
                id: realMemberData?.id || 'admin-user',
                email: user.email,
                name: realMemberData?.name || user.displayName || user.email?.split('@')[0] || 'Administrador',
                isAdmin: true,
                role: realMemberData?.role || 'Administrador',
                status: 'Ativo'
            };
            if (impersonatedUser) return;
            setAuthData({ user, isAdmin: true, currentUserData: realMemberData ? { ...realMemberData, isAdmin: true } : fallbackUserData });
        }
    }, [tenantData, user, isAdmin, allMembers, setAuthData, impersonatedUser]);

    // CORRIGIDO: useEffect 3 - Para buscar os outros dados (Connects, Cursos, etc.)
    useEffect(() => {
        if (!user || isKidsRoute) {
            setLoadingData(false);
            // Mantém dados de exemplo se o usuário fizer logout ou estiver no Kids
            setAllConnects([]);
            setAllCourses([]);
            setAllCourseTemplates([]);
            setAllCertificateTemplates([]);
            setAllConnectReports([]);
            return;
        }
        setLoadingData(true);
        const unsubs = [
            onSnapshot(
                collection(db, `artifacts/${getTenantId()}/public/data/connects`),
                (s) => {
                    const connectsList = s.docs.map(d => ({ id: d.id, ...d.data() }));
                    // Se não há dados no Firebase, usa dados de exemplo
                    const finalConnectsList = connectsList;
                    setAllConnects(finalConnectsList);
                },
                (error) => {
                    console.error('Erro ao carregar connects:', error);
                    setConnectionError('Erro de conexão: Não foi possível carregar os dados dos connects. ');
                    setAllConnects([]); // Usa dados de exemplo em caso de erro
                }
            ),
            onSnapshot(
                collection(db, `artifacts/${getTenantId()}/public/data/courses`),
                (s) => {
                    const coursesList = s.docs.map(d => ({ id: d.id, ...d.data() }));
                    const finalCoursesList = coursesList;
                    setAllCourses(finalCoursesList);
                },
                (error) => {
                    console.error('Erro ao carregar cursos:', error);
                    setConnectionError('Erro de conexão: Não foi possível carregar os dados dos cursos. ');
                    setAllCourses([]);
                }
            ),
            onSnapshot(
                query(collection(db, `artifacts/${getTenantId()}/public/data/courseTemplates`)),
                async (s) => {
                    const templatesList = s.docs.map(d => ({ id: d.id, ...d.data() }));
                    setAllCourseTemplates(templatesList);

                    // Garantir que o modelo padrão de Aliança de Membresia existe e está associado ao marco 'membership'
                    const hasMembershipTemplate = templatesList.some(t =>
                        t.linkedMilestoneId === 'membership' ||
                        (t.name && (t.name.toLowerCase().includes('aliança') || t.name.toLowerCase().includes('alianca')) && t.name.toLowerCase().includes('membresia'))
                    );

                    if (!hasMembershipTemplate && s.docs !== undefined) {
                        try {
                            const defaultTemplate = {
                                name: 'Aliança de Membresia',
                                description: 'Curso de Aliança de Membresia associado ao Trilho de Liderança.',
                                linkedMilestoneId: 'membership',
                                order: 1,
                                assessment: {
                                    tests: { count: 0, value: 0 },
                                    assignments: { count: 0, value: 0 },
                                    activities: { count: 0, value: 0 }
                                },
                                passingCriteria: { minGrade: 7, minAttendance: 75 },
                                lessonsCount: 4,
                                lessonPlan: [
                                    { activityIndex: null, testIndex: null, assignmentIndex: null, notes: 'Aula 1: A Igreja e sua Visão', considerAttendance: true },
                                    { activityIndex: null, testIndex: null, assignmentIndex: null, notes: 'Aula 2: Valores e Princípios Bíblicos', considerAttendance: true },
                                    { activityIndex: null, testIndex: null, assignmentIndex: null, notes: 'Aula 3: Compromisso e Aliança', considerAttendance: true },
                                    { activityIndex: null, testIndex: null, assignmentIndex: null, notes: 'Aula 4: Ministérios e Próximos Passos', considerAttendance: true }
                                ],
                                createdAt: new Date().toISOString()
                            };
                            await setDoc(doc(db, `artifacts/${getTenantId()}/public/data/courseTemplates`, 'template_alianca_membresia'), defaultTemplate, { merge: true });
                        } catch (err) {
                            console.warn('Não foi possível inicializar o modelo de Aliança de Membresia:', err);
                        }
                    }
                },
                (error) => {
                    console.error('Erro ao carregar templates de curso:', error);
                    setConnectionError('Erro de conexão: Não foi possível carregar os templates de curso. ');
                    setAllCourseTemplates([]);
                }
            ),
            onSnapshot(
                query(collection(db, `artifacts/${getTenantId()}/public/data/certificate_templates`)),
                (s) => {
                    const templatesList = s.docs.map(d => ({ id: d.id, ...d.data() }));
                    setAllCertificateTemplates(templatesList);
                },
                (error) => {
                    console.error('Erro ao carregar templates de certificado:', error);
                    setConnectionError('Erro de conexão: Não foi possível carregar os templates de certificado.');
                    setAllCertificateTemplates([]);
                }
            ),
            onSnapshot(
                collection(db, `artifacts/${getTenantId()}/public/data/connect_reports`),
                (s) => {
                    const reportsList = s.docs.map(d => ({ id: d.id, ...d.data() }));
                    const finalReportsList = reportsList;
                    setAllConnectReports(finalReportsList);
                },
                (error) => {
                    console.error('Erro ao carregar relatórios:', error);
                    setConnectionError('Erro de conexão: Não foi possível carregar os relatórios. ');
                    setAllConnectReports([]);
                }
            ),
            onSnapshot(
                collection(db, `artifacts/${getTenantId()}/public/data/decisions`),
                (s) => {
                    const decisionsList = s.docs.map(d => ({ id: d.id, ...d.data() }));
                    setAllDecisions(decisionsList);
                },
                (error) => {
                    console.error('Erro ao carregar decisões:', error);
                    setAllDecisions([]);
                }
            ),
            onSnapshot(
                collection(db, `artifacts/${getTenantId()}/public/data/course_members`),
                (s) => {
                    const list = s.docs.map(d => {
                        const data = d.data();
                        return { 
                            id: d.id, 
                            ...data, 
                            name: data.lastName ? `${data.name} ${data.lastName}`.trim() : data.name 
                        };
                    });
                    setAllSimpleMembers(list);
                },
                (error) => {
                    console.error('Erro ao carregar cadastros simples:', error);
                }
            )
        ];
        // Remove artificial delay for better performance
        setLoadingData(false);
        return () => unsubs.forEach(unsub => unsub());
    }, [user]);

    // Auto-reconciliação para recuperar dados já lançados que sofreram deslocamento de data (+1 dia por fuso UTC após as 21h)
    useEffect(() => {
        if (!allConnectReports.length || !allConnects.length) return;

        const reconcileShiftedReports = async () => {
            for (const report of allConnectReports) {
                const connect = allConnects.find(c => String(c.id) === String(report.connectId));
                if (!connect || !connect.weekday) continue;

                const connectWeekday = getConnectWeekdayIndex(connect.weekday);
                if (connectWeekday === null) continue;

                const reportDateObj = report.reportDate?.toDate ? report.reportDate.toDate() : new Date(report.reportDate);
                if (isNaN(reportDateObj.getTime())) continue;

                // Neutraliza horário para meio-dia
                reportDateObj.setHours(12, 0, 0, 0);
                const reportWeekday = reportDateObj.getDay();

                // Se a data do relatório caiu exatamente no dia seguinte ao dia da reunião do Connect (ex: sexta em vez de quinta)
                if (reportWeekday === (connectWeekday + 1) % 7) {
                    const prevDate = new Date(reportDateObj);
                    prevDate.setDate(prevDate.getDate() - 1);
                    const correctIso = formatLocalDateToISO(prevDate);
                    const correctReportId = `${connect.id}_${correctIso}`;

                    const existingCorrect = allConnectReports.find(r => r.id === correctReportId);

                    try {
                        const correctRef = doc(db, `artifacts/${getTenantId()}/public/data/connect_reports`, correctReportId);
                        const oldRef = doc(db, `artifacts/${getTenantId()}/public/data/connect_reports`, report.id);

                        if (!existingCorrect) {
                            const { id, ...dataToSave } = report;
                            await setDoc(correctRef, {
                                ...dataToSave,
                                reportDate: new Date(correctIso + 'T12:00:00Z'),
                                updatedAt: new Date()
                            }, { merge: true });
                        }

                        // Remove o documento com ID deslocado para evitar registros fantasmas
                        if (report.id !== correctReportId) {
                            await deleteDoc(oldRef);
                        }
                    } catch (migrationErr) {
                        console.warn("Aviso ao reconciliar relatório deslocado no App:", migrationErr);
                    }
                }
            }
        };

        reconcileShiftedReports();
    }, [allConnectReports, allConnects]);

    const combinedMembers = useMemo(() => {
        const simpleMapped = allSimpleMembers.map(sm => ({
            ...sm,
            isSimple: true,
            email: sm.email || '',
            phone: sm.phone || '',
            dob: sm.dob || ''
        }));
        return [...allMembers, ...simpleMapped];
    }, [allMembers, allSimpleMembers]);

    // O resto do arquivo permanece o mesmo...

    const { visibleMembers, visibleConnects, visibleCourses, visibleReports } = useMemo(() => {
        if (!user || !currentUserData) return { visibleMembers: [], visibleConnects: [], visibleCourses: [], visibleReports: [] };

        if (isAdmin) {
            return { visibleMembers: allMembers, visibleConnects: allConnects, visibleCourses: allCourses, visibleReports: allConnectReports };
        }

        const userEmail = currentUserData.email?.toLowerCase();
        const supervisedConnects = allConnects.filter(c => c.supervisorEmail?.toLowerCase() === userEmail);
        const ledConnects = allConnects.filter(c => c.leaderEmail?.toLowerCase() === userEmail);

        // Função para verificar se o usuário é professor substituto ativo
        const isActiveSubstitute = (course) => {
            if (!course.substituteTeacher || !course.substituteTeacher.teacherId) return false;

            const substituteTeacher = allMembers.find(m => m.id === course.substituteTeacher.teacherId);
            if (!substituteTeacher || substituteTeacher.email?.toLowerCase() !== userEmail) return false;

            const today = new Date();
            const startDate = new Date(course.substituteTeacher.startDate);

            // Se é indefinido, verifica apenas se já começou
            if (course.substituteTeacher.isIndefinite) {
                return today >= startDate;
            }

            // Se tem data de fim, verifica se está no período
            const endDate = new Date(course.substituteTeacher.endDate);
            return today >= startDate && today <= endDate;
        };

        const taughtCourses = allCourses.filter(c =>
            c.teacherEmail?.toLowerCase() === userEmail || isActiveSubstitute(c)
        );

        const auxConnects = allConnects.filter(c => {
            const isAux = (
                Array.isArray(c.auxLeaders) && c.auxLeaders.some(l => l.id === currentUserData?.id || (l.email || '').toLowerCase() === userEmail)
            ) || c.auxLeaderId === currentUserData?.id || (c.auxLeaderEmail || '').toLowerCase() === userEmail;
            return isAux;
        });

        const visibleConnectsSet = new Set([...supervisedConnects, ...ledConnects, ...auxConnects]);
        const visibleConnects = Array.from(visibleConnectsSet);
        const visibleConnectIds = visibleConnects.map(c => c.id);

        const visibleMembers = allMembers.filter(m => visibleConnectIds.includes(m.connectId));
        const visibleReports = allConnectReports.filter(r => visibleConnectIds.includes(r.connectId));

        return { visibleMembers, visibleConnects, visibleCourses: taughtCourses, visibleReports };

    }, [user, isAdmin, currentUserData, allMembers, allConnects, allCourses, allConnectReports]);

    const attendanceAlerts = useMemo(() => {
        const alerts = [];
        if (!allMembers.length || !allConnectReports.length) {
            return alerts;
        }

        // Função para verificar se um membro estava em um Connect em uma data específica
        const wasMemberInConnectAtDate = (member, connectId, date) => {
            // Se o membro está atualmente no Connect
            if (member.connectId === connectId) {
                // Verifica se já estava no Connect na data
                if (member.connectHistory && member.connectHistory.length > 0) {
                    const currentEntry = member.connectHistory.find(entry => !entry.endDate);
                    if (currentEntry) {
                        const startDate = currentEntry.startDate.toDate ? currentEntry.startDate.toDate() : new Date(currentEntry.startDate);
                        return date >= startDate;
                    }
                }
                return true; // Se não tem histórico, considera que sempre esteve
            }

            // Se o membro não está atualmente no Connect, verifica o histórico
            if (member.connectHistory && member.connectHistory.length > 0) {
                return member.connectHistory.some(entry => {
                    if (entry.connectId !== connectId) return false;

                    const startDate = entry.startDate.toDate ? entry.startDate.toDate() : new Date(entry.startDate);
                    const endDate = entry.endDate ? (entry.endDate.toDate ? entry.endDate.toDate() : new Date(entry.endDate)) : null;

                    return date >= startDate && (!endDate || date <= endDate);
                });
            }

            return false;
        };

        const sortedReports = [...allConnectReports].sort((a, b) => {
            const dateA = a.reportDate.toDate ? a.reportDate.toDate() : new Date(a.reportDate);
            const dateB = b.reportDate.toDate ? b.reportDate.toDate() : new Date(b.reportDate);
            return dateB - dateA;
        });

        // Agrupa membros por Connect atual
        const membersByConnect = allMembers.reduce((acc, member) => {
            if (member.connectId) {
                if (!acc[member.connectId]) acc[member.connectId] = [];
                acc[member.connectId].push(member);
            }
            return acc;
        }, {});

        for (const connectId in membersByConnect) {
            const members = membersByConnect[connectId];
            const reportsForConnect = sortedReports.filter(r => r.connectId === connectId);

            if (reportsForConnect.length < 4) continue;

            for (const member of members) {
                let consecutiveAbsences = 0;
                for (let i = 0; i < 4; i++) {
                    if (!reportsForConnect[i]) break;
                    const report = reportsForConnect[i];
                    const reportDate = report.reportDate.toDate ? report.reportDate.toDate() : new Date(report.reportDate);

                    // Verifica se o membro estava no Connect na data do relatório
                    if (wasMemberInConnectAtDate(member, connectId, reportDate)) {
                        const attendanceStatus = report.attendance?.[member.id];
                        if (attendanceStatus === 'ausente') {
                            consecutiveAbsences++;
                        } else {
                            break;
                        }
                    }
                }

                if (consecutiveAbsences >= 4) {
                    let totalConsecutiveAbsences = 0;
                    for (const report of reportsForConnect) {
                        const reportDate = report.reportDate.toDate ? report.reportDate.toDate() : new Date(report.reportDate);

                        // Verifica se o membro estava no Connect na data do relatório
                        if (wasMemberInConnectAtDate(member, connectId, reportDate)) {
                            const attendanceStatus = report.attendance?.[member.id];
                            if (attendanceStatus === 'ausente') {
                                totalConsecutiveAbsences++;
                            } else {
                                break;
                            }
                        }
                    }

                    // Obter nome conhecido do membro
                    const knownName = member.knownBy || member.name?.split(' ')[0] || member.name || 'Membro';

                    alerts.push({
                        memberId: member.id,
                        memberName: knownName,
                        memberPhone: member.phone, // Adicionado telefone do membro
                        connectId: connectId,
                        absences: totalConsecutiveAbsences,
                        status: totalConsecutiveAbsences >= 6 ? 'inactive' : 'alert'
                    });
                }
            }
        }
        return alerts;
    }, [allMembers, allConnectReports]);

    const openMemberModal = (member = null) => { setEditingMember(member); setMemberModalOpen(true); };
    const closeMemberModal = () => setMemberModalOpen(false);
    const openConnectModal = (connect = null) => { setEditingConnect(connect); setConnectModalOpen(true); };
    const closeConnectModal = () => setConnectModalOpen(false);
    const openCourseModal = (course = null) => { setEditingCourse(course); setCourseModalOpen(true); };
    const closeCourseModal = () => { setEditingCourse(null); setCourseModalOpen(false); };
    const openCourseTemplateModal = (template = null) => { setEditingCourseTemplate(template); setCourseTemplateModalOpen(true); };
    const closeCourseTemplateModal = () => { setEditingCourseTemplate(null); setCourseTemplateModalOpen(false); };
    const openManageCourseModal = (course, initialTab = 'attendance') => { 
        setManagingCourse({ ...course, initialTab }); 
        setManageCourseModalOpen(true); 
    };
    const closeManageCourseModal = () => setManageCourseModalOpen(false);
    const openReportModal = (connect) => { setReportingConnect(connect); setReportModalOpen(true); };
    const closeReportModal = () => setReportModalOpen(false);
    const openConnectFullReportModal = (connect) => { setGeneratingReportForConnect(connect); setConnectFullReportModalOpen(true); };
    const closeConnectFullReportModal = () => setConnectFullReportModalOpen(false);

    // Visualização de cadastro (somente leitura)
    const [isMemberDetailsOpen, setMemberDetailsOpen] = useState(false);
    const openMemberDetails = (member) => { if (!member) return; setViewingMember(member); setMemberDetailsOpen(true); };
    const closeMemberDetails = () => { setMemberDetailsOpen(false); setViewingMember(null); };


    const openLeadershipTrackModal = async (member) => {
        if (!member) return;
        const currentMember = allMembers.find(m => m.id === member.id) || member;
        setViewingMember(currentMember);

        try {
            const coursesRef = collection(db, `artifacts/${getTenantId()}/public/data/members/${member.id}/completedCourses`);
            const querySnapshot = await getDocs(coursesRef);
            const courses = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setCompletedCourses(courses);

            let historyDetails = [];
            if (member.connectHistory && member.connectHistory.length > 0) {
                for (const historyEntry of member.connectHistory) {
                    const connectName = getConnectName(historyEntry.connectId);
                    const relevantReports = allConnectReports.filter(report => {
                        const reportDate = report.reportDate.toDate ? report.reportDate.toDate() : new Date(report.reportDate);
                        const startDate = historyEntry.startDate.toDate ? historyEntry.startDate.toDate() : new Date(historyEntry.startDate);
                        const endDate = historyEntry.endDate ? (historyEntry.endDate.toDate ? historyEntry.endDate.toDate() : new Date(historyEntry.endDate)) : null;

                        return report.connectId === historyEntry.connectId &&
                            reportDate >= startDate &&
                            (!endDate || reportDate <= endDate);
                    });

                    let presenceCount = 0;
                    let absenceCount = 0;
                    relevantReports.forEach(report => {
                        const status = report.attendance?.[member.id];
                        if (status === 'presente' || status === 'justificado') {
                            presenceCount++;
                        } else if (status === 'ausente') {
                            absenceCount++;
                        }
                    });

                    historyDetails.push({
                        ...historyEntry,
                        connectName,
                        presenceCount,
                        absenceCount
                    });
                }
            }
            setMemberConnectHistoryDetails(historyDetails.sort((a, b) => (b.startDate.toDate ? b.startDate.toDate() : new Date(b.startDate)) - (a.startDate.toDate ? a.startDate.toDate() : new Date(a.startDate))));

        } catch (error) {
            console.error("Erro ao buscar dados para o Trilho de Liderança:", error);
            setCompletedCourses([]);
            setMemberConnectHistoryDetails([]);
        }

        setLeadershipTrackModalOpen(true);
    };
    const closeLeadershipTrackModal = () => {
        setLeadershipTrackModalOpen(false);
        setViewingMember(null);
        setCompletedCourses([]);
        setMemberConnectHistoryDetails([]);
    };

    const openConnectTrackPage = async (connect) => {
        if (!connect) return;

        // Navegar para a página do trilho de Connect
        navigate(`/connect-track/${connect.id}`);
    };
    const triggerDelete = (type, id) => {
        let message = "Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.";
        if (type === 'connect') {
            message = "Atenção! Excluir este Connect removerá a associação de todos os membros a ele. Deseja continuar?";
        }
        setDeleteAction({ type, id, message });
        setConfirmModalOpen(true);
    };

    const handleLogout = async () => { await signOut(auth); clearAuthData(); };

    const handleSaveMember = async (memberData, originalMember = null) => {
        const collectionPath = `artifacts/${getTenantId()}/public/data/members`;
        const today = new Date();

        loadingStates.setLoading('saveMember', 'Salvando membro...');

        try {
            let dataToSave = { ...memberData };

            if (originalMember && originalMember.connectId !== dataToSave.connectId) {
                const history = originalMember.connectHistory || [];
                const lastEntry = history.find(entry => !entry.endDate);

                if (lastEntry) {
                    lastEntry.endDate = today;
                }

                if (dataToSave.connectId) {
                    history.push({ connectId: dataToSave.connectId, startDate: today, endDate: null });
                }
                dataToSave.connectHistory = history;
            } else if (!originalMember && dataToSave.connectId) {
                dataToSave.connectHistory = [{ connectId: dataToSave.connectId, startDate: today, endDate: null }];
            }

            if (editingMember || originalMember) {
                const memberId = editingMember?.id || originalMember?.id;
                const currentMemberInState = allMembers.find(m => m.id === memberId);
                if (!dataToSave.milestones && currentMemberInState?.milestones) {
                    dataToSave.milestones = currentMemberInState.milestones;
                }
                await setDoc(doc(db, collectionPath, memberId), dataToSave);
            } else {
                await addDoc(collection(db, collectionPath), dataToSave);
            }

            loadingStates.setSuccess('saveMember', 'Membro salvo com sucesso!');
            closeMemberModal();
        } catch (error) {
            console.error("Erro ao salvar membro:", error);
            loadingStates.setError('saveMember', 'Erro ao salvar membro. Tente novamente.');
        }
    };

    const handleSaveSimpleMember = async (simpleMemberData, editingSimpleMember = null) => {
        const collectionPath = `artifacts/${getTenantId()}/public/data/course_members`;
        loadingStates.setLoading('saveSimpleMember', 'Salvando cadastro simples...');
        try {
            let resultRef = null;
            if (editingSimpleMember) {
                await setDoc(doc(db, collectionPath, editingSimpleMember.id), simpleMemberData);
                resultRef = { id: editingSimpleMember.id };
            } else {
                resultRef = await addDoc(collection(db, collectionPath), simpleMemberData);
            }
            loadingStates.setSuccess('saveSimpleMember', 'Cadastro simples salvo!');
            return resultRef;
        } catch (error) {
            console.error("Erro ao salvar cadastro simples:", error);
            loadingStates.setError('saveSimpleMember', 'Erro ao salvar. Tente novamente.');
            throw error;
        }
    };

    const handleDeleteSimpleMember = async (simpleMemberId) => {
        const docRef = doc(db, `artifacts/${getTenantId()}/public/data/course_members`, simpleMemberId);
        loadingStates.setLoading('deleteSimpleMember', 'Excluindo cadastro simples...');
        try {
            await deleteDoc(docRef);
            loadingStates.setSuccess('deleteSimpleMember', 'Cadastro simples excluído!');
        } catch (error) {
            console.error("Erro ao excluir cadastro simples:", error);
            loadingStates.setError('deleteSimpleMember', 'Erro ao excluir.');
        }
    };

    const handleMergeSimpleMemberToFull = async (simpleMemberId, fullMemberId, fullMemberName) => {
        try {
            console.log(`🔀 Iniciando mesclagem: ${simpleMemberId} -> ${fullMemberId}`);
            const batch = writeBatch(db);

            // 1. Procurar cursos em que o cadastro simplificado está inscrito
            const coursesToUpdate = allCourses.filter(course => 
                Array.isArray(course.students) && course.students.some(s => s.id === simpleMemberId)
            );

            for (const course of coursesToUpdate) {
                // Atualiza a lista de estudantes na turma
                const updatedStudents = course.students.map(s => {
                    if (s.id === simpleMemberId) {
                        return { ...s, id: fullMemberId, name: fullMemberName };
                    }
                    return s;
                });

                const courseRef = doc(db, `artifacts/${getTenantId()}/public/data/courses`, course.id);
                batch.update(courseRef, { students: updatedStudents });

                // Migrar o histórico de presença
                const attendanceRef = collection(db, `artifacts/${getTenantId()}/public/data/courses/${course.id}/attendance`);
                const attendanceSnapshot = await getDocs(attendanceRef);
                
                attendanceSnapshot.forEach(attendanceDoc => {
                    const data = attendanceDoc.data();
                    if (data && data.statuses && simpleMemberId in data.statuses) {
                        const updatedStatuses = { ...data.statuses };
                        updatedStatuses[fullMemberId] = updatedStatuses[simpleMemberId];
                        delete updatedStatuses[simpleMemberId];

                        batch.update(attendanceDoc.ref, { statuses: updatedStatuses });
                    }
                });
            }

            // 2. Excluir o cadastro simples original
            const simpleMemberRef = doc(db, `artifacts/${getTenantId()}/public/data/course_members`, simpleMemberId);
            batch.delete(simpleMemberRef);

            // Executar todas as gravações em lote de forma atômica
            await batch.commit();
            console.log('🔀 Mesclagem concluída com sucesso!');
        } catch (error) {
            console.error("Erro ao realizar mesclagem:", error);
            alert("Ocorreu um erro ao migrar o histórico escolar da pessoa unificada.");
        }
    };

    const handleCheckDuplicate = async (newMemberData) => {
        const SIMILARITY_THRESHOLD = 0.8;
        const newEmail = (newMemberData.email || '').toLowerCase().trim();
        const newPhone = (newMemberData.phone || '').replace(/\D/g, '');
        const newDob = newMemberData.dob;
        let potentialDuplicate = null;

        // 1. Procurar em membros completos
        for (const existingMember of allMembers) {
            if (!existingMember.name) continue;
            const existingEmail = existingMember.email?.toLowerCase().trim();
            const existingPhone = existingMember.phone?.replace(/\D/g, '');
            const existingDob = existingMember.dob;
            const isEmailMatch = newEmail && newEmail === existingEmail;
            const isPhoneMatch = newPhone && newPhone === existingPhone;
            const isDobMatch = newDob && newDob === existingDob;
            if (isEmailMatch || isPhoneMatch || isDobMatch) {
                const isNameSimilar = areNamesSimilar(newMemberData.name, existingMember.name, SIMILARITY_THRESHOLD);
                if (isNameSimilar) {
                    potentialDuplicate = existingMember;
                    break;
                }
            }
        }

        // 2. Procurar em cadastros simples (pelo nome)
        if (!potentialDuplicate) {
            for (const simpleMember of allSimpleMembers) {
                if (!simpleMember.name) continue;
                const isNameSimilar = areNamesSimilar(newMemberData.name, simpleMember.name, SIMILARITY_THRESHOLD);
                if (isNameSimilar) {
                    potentialDuplicate = { ...simpleMember, isSimple: true };
                    break;
                }
            }
        }

        if (potentialDuplicate) {
            setExistingMemberForCheck(potentialDuplicate);
            setNewMemberForCheck(newMemberData);
            setDuplicateModalOpen(true);
            closeMemberModal();
        } else {
            handleSaveMember(newMemberData, null);
        }
    };

    const handleRejectDuplicate = () => {
        handleSaveMember(newMemberForCheck, null);
        setDuplicateModalOpen(false);
    };

    const handleConfirmDuplicate = async () => {
        if (existingMemberForCheck?.isSimple) {
            // Unificação: Salvar o novo membro completo e migrar o histórico escolar do simples
            const collectionPath = `artifacts/${getTenantId()}/public/data/members`;
            const today = new Date();
            let dataToSave = { ...newMemberForCheck };
            if (dataToSave.connectId) {
                dataToSave.connectHistory = [{ connectId: dataToSave.connectId, startDate: today, endDate: null }];
            }
            try {
                const docRef = await addDoc(collection(db, collectionPath), dataToSave);
                const newMemberId = docRef.id;
                
                await handleMergeSimpleMemberToFull(existingMemberForCheck.id, newMemberId, dataToSave.name);
                alert(`O cadastro simplificado de "${existingMemberForCheck.name}" foi promovido a Membro Completo e todo o histórico de cursos e presenças foi transferido com sucesso.`);
            } catch (error) {
                console.error("Erro na unificação:", error);
                alert("Erro ao unificar cadastros.");
            }
        } else if (existingMemberForCheck?.connectId) {
            const connect = allConnects.find(c => c.id === existingMemberForCheck.connectId);
            const leader = allMembers.find(m => m.id === connect?.leaderId);
            alert(`Este membro já pertence ao Connect ${connect?.number} - ${connect?.name}.\n\nPor favor, entre em contato com o líder: ${leader?.name} (${leader?.phone}) para atualizar o cadastro existente.`);
        } else {
            alert(`Este membro já está no sistema. Para evitar a criação de uma duplicata, por favor, feche esta janela e edite o cadastro existente de "${existingMemberForCheck.name}" na lista de membros.`);
        }
        setDuplicateModalOpen(false);
    };

    // Função de retry para operações do Firestore com backoff exponencial e detecção de erro transitório
    const retryFirestoreOperation = async (
        operation,
        maxRetries = 5,
        baseDelayMs = 800,
    ) => {
        const isTransientError = (err) => {
            const code = err?.code;
            const msg = String(err?.message || '');
            // Erros comuns de rede/latência que valem retry
            const transientCodes = ['unavailable', 'deadline-exceeded', 'aborted'];
            const transientMsgPattern = /(ERR_HTTP2_PROTOCOL_ERROR|Failed to load resource|network|quota|timeout)/i;
            return transientCodes.includes(code) || transientMsgPattern.test(msg);
        };

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                console.log(`🔄 Tentativa ${attempt}/${maxRetries} da operação Firestore`);
                return await operation();
            } catch (error) {
                const online = typeof navigator !== 'undefined' ? navigator.onLine : true;
                console.error(`âŒ Erro na tentativa ${attempt}:`, error);

                // Se for erro permanente (não transitório) ou está offline, não insiste
                if (!isTransientError(error) || !online) {
                    console.warn('⛔ Erro não transitório ou offline detectado. Interrompendo retries.');
                    throw error;
                }

                if (attempt === maxRetries) {
                    throw error;
                }

                // Backoff exponencial com jitter
                const jitter = Math.floor(Math.random() * 400);
                const delayMs = baseDelayMs * Math.pow(2, attempt - 1) + jitter;
                console.log(`â³ Aguardando ${delayMs}ms antes da nova tentativa...`);
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }
        }
    };

    // Definir (adicionar) Líder Auxiliar para um Connect (suporta múltiplos auxiliares)
    const handleSetAuxLeader = async (connect, memberId) => {
        try {
            const userEmail = (currentUserData?.email || '').toLowerCase();
            const canSet = isAdmin ||
                connect.leaderId === currentUserData?.id || (connect.leaderEmail || '').toLowerCase() === userEmail ||
                (connect.supervisorEmail || '').toLowerCase() === userEmail;
            if (!canSet) {
                setOperationStatus({ type: 'error', message: 'Você não tem permissão para definir Líder Auxiliar deste Connect.' });
                return;
            }

            const auxMember = allMembers.find(m => m.id === memberId);
            if (!auxMember || !auxMember.email) {
                setOperationStatus({ type: 'error', message: 'Seleção inválida. O membro precisa ter e-mail cadastrado.' });
                return;
            }

            // Preparar lista atual de auxiliares (compatível com documentos legados)
            const existingAux = Array.isArray(connect.auxLeaders) ? connect.auxLeaders : [];
            const alreadyAux = existingAux.some(l => l.id === auxMember.id || (l.email || '').toLowerCase() === (auxMember.email || '').toLowerCase());
            if (alreadyAux) {
                setOperationStatus({ type: 'error', message: 'Este membro já é Líder Auxiliar neste Connect.' });
                return;
            }

            const updatedAux = [...existingAux, { id: auxMember.id, email: auxMember.email, name: auxMember.name }];

            loadingStates.setLoading('saveConnect', 'Definindo Líder Auxiliar...');
            await retryFirestoreOperation(async () => {
                const ref = doc(db, `artifacts/${getTenantId()}/public/data/connects`, connect.id);
                await updateDoc(ref, {
                    // Novo modelo com múltiplos auxiliares
                    auxLeaders: updatedAux,
                    // Manter campos legados se ainda não definidos (compatibilidade)
                    auxLeaderId: connect.auxLeaderId || (updatedAux[0]?.id || ''),
                    auxLeaderEmail: connect.auxLeaderEmail || (updatedAux[0]?.email || ''),
                    auxLeaderName: connect.auxLeaderName || (updatedAux[0]?.name || ''),
                });
            });

            loadingStates.setSuccess('saveConnect', 'Líder Auxiliar adicionado com sucesso!');
        } catch (error) {
            console.error('Erro ao definir Líder Auxiliar:', error);
            loadingStates.setError('saveConnect', 'Erro ao definir Líder Auxiliar.');
        }
    };

    // Remover Líder Auxiliar de um Connect
    const handleRemoveAuxLeader = async (connect, memberId) => {
        try {
            const userEmail = (currentUserData?.email || '').toLowerCase();
            const canSet = isAdmin ||
                connect.leaderId === currentUserData?.id || (connect.leaderEmail || '').toLowerCase() === userEmail ||
                (connect.supervisorEmail || '').toLowerCase() === userEmail;
            if (!canSet) {
                setOperationStatus({ type: 'error', message: 'Você não tem permissão para remover Líder Auxiliar deste Connect.' });
                return;
            }

            const existingAux = Array.isArray(connect.auxLeaders) ? connect.auxLeaders : [];
            const newAux = existingAux.filter(l => l.id !== memberId);

            loadingStates.setLoading('saveConnect', 'Removendo Líder Auxiliar...');
            await retryFirestoreOperation(async () => {
                const ref = doc(db, `artifacts/${getTenantId()}/public/data/connects`, connect.id);
                const legacyUpdate = {};
                // Atualizar campos legados se o removido era o auxiliar legado
                if (connect.auxLeaderId === memberId) {
                    legacyUpdate.auxLeaderId = newAux[0]?.id || '';
                    legacyUpdate.auxLeaderEmail = newAux[0]?.email || '';
                    legacyUpdate.auxLeaderName = newAux[0]?.name || '';
                }
                await updateDoc(ref, {
                    auxLeaders: newAux,
                    ...legacyUpdate,
                });
            });

            loadingStates.setSuccess('saveConnect', 'Líder Auxiliar removido com sucesso!');
        } catch (error) {
            console.error('Erro ao remover Líder Auxiliar:', error);
            loadingStates.setError('saveConnect', 'Erro ao remover Líder Auxiliar.');
        }
    };

    // Definir Auxiliar de Professor para uma Turma (somente Admin, Professor ou Substituto Ativo da Turma)
    const handleSetAuxTeacher = async (course, memberId) => {
        try {
            const userEmail = (currentUserData?.email || '').toLowerCase();
            const isSubActive = () => {
                try {
                    if (!course?.substituteTeacher?.teacherId || !Array.isArray(allMembers) || !userEmail) return false;
                    const substituteTeacher = allMembers.find(m => m.id === course.substituteTeacher.teacherId);
                    if (!substituteTeacher || (substituteTeacher.email || '').toLowerCase() !== userEmail) return false;
                    const today = new Date();
                    const startDate = new Date(course.substituteTeacher.startDate);
                    if (course.substituteTeacher.isIndefinite) return today >= startDate;
                    const endDate = new Date(course.substituteTeacher.endDate);
                    return today >= startDate && today <= endDate;
                } catch {
                    return false;
                }
            };
            const canSet = isAdmin || ((course.teacherEmail || '').toLowerCase() === userEmail) || isSubActive();
            if (!canSet) {
                setOperationStatus({ type: 'error', message: 'Você não tem permissão para definir Auxiliar nesta turma.' });
                return;
            }

            const auxMember = allMembers.find(m => m.id === memberId);
            if (!auxMember || !auxMember.email) {
                setOperationStatus({ type: 'error', message: 'Seleção inválida. O auxiliar precisa ter e-mail cadastrado.' });
                return;
            }

            // O auxiliar deve ser um aluno matriculado nesta turma
            const isEnrolled = Array.isArray(course?.students) && course.students.some(s => s.id === auxMember.id);
            if (!isEnrolled) {
                setOperationStatus({ type: 'error', message: 'O auxiliar deve ser um aluno matriculado nesta turma.' });
                return;
            }

            loadingStates.setLoading('saveCourse', 'Definindo Auxiliar de Professor...');
            await retryFirestoreOperation(async () => {
                const ref = doc(db, `artifacts/${getTenantId()}/public/data/courses`, course.id);
                await updateDoc(ref, {
                    auxTeacherId: auxMember.id,
                    auxTeacherEmail: auxMember.email,
                    auxTeacherName: auxMember.name,
                });
            });

            loadingStates.setSuccess('saveCourse', 'Auxiliar de Professor definido com sucesso!');
        } catch (error) {
            console.error('Erro ao definir Auxiliar de Professor:', error);
            loadingStates.setError('saveCourse', 'Erro ao definir Auxiliar de Professor.');
        }
    };

    const handleSaveConnect = async (connectData) => {
        const collectionPath = `artifacts/${getTenantId()}/public/data/connects`;

        console.log('🔄 Iniciando salvamento do Connect:', connectData);
        loadingStates.setLoading('saveConnect', 'Salvando connect...');

        try {
            let connectId = editingConnect?.id;
            console.log('ðŸ“ Connect ID:', connectId, 'Editando:', !!editingConnect);

            // Validações básicas
            if (!connectData.leaderId) {
                throw new Error('Líder é obrigatório');
            }

            const leader = allMembers.find(m => m.id === connectData.leaderId);
            if (!leader) {
                throw new Error('Líder selecionado não encontrado');
            }

            console.log('👤 Líder encontrado:', leader.name);

            // Buscar membros atuais do Connect (se editando)
            let currentMemberIds = [];
            if (editingConnect) {
                currentMemberIds = allMembers
                    .filter(m => m.connectId === editingConnect.id)
                    .map(m => m.id);
                console.log('👥 Membros atuais do Connect:', currentMemberIds.length);
            }

            // Salvar o Connect
            console.log('💾 Salvando dados do Connect...');
            if (editingConnect) {
                await retryFirestoreOperation(async () => {
                    await setDoc(doc(db, collectionPath, connectId), connectData);
                });
                console.log('✅ Connect atualizado com sucesso');
            } else {
                // Verificar se já existe um Connect com este número
                console.log('ðŸ” Verificando duplicação de número...');
                const querySnapshot = await retryFirestoreOperation(async () => {
                    const q = query(collection(db, collectionPath), where("number", "==", connectData.number));
                    return await getDocs(q);
                });

                if (!querySnapshot.empty) {
                    console.log('âŒ Connect com número duplicado encontrado');
                    loadingStates.setError('saveConnect', 'Já existe um Connect com este número.');
                    return;
                }
                console.log('✅ Número do Connect disponível');

                const newDocRef = await retryFirestoreOperation(async () => {
                    return await addDoc(collection(db, collectionPath), connectData);
                });
                connectId = newDocRef.id;
                console.log('✅ Novo Connect criado com ID:', connectId);
            }

            // Usar batch para sincronizar membros
            console.log('🔄 Iniciando sincronização de membros...');
            const batch = writeBatch(db);

            // Remover connectId dos membros que não estão mais no Connect
            const membersToRemove = currentMemberIds.filter(id => !connectData.memberIds.includes(id));
            const today = new Date();

            console.log('➖ Membros a remover:', membersToRemove.length);
            membersToRemove.forEach(memberId => {
                const member = allMembers.find(m => m.id === memberId);
                if (member) {
                    console.log('🔄 Removendo membro:', member.name);
                    const history = member.connectHistory || [];
                    const lastEntry = history.find(entry => !entry.endDate);

                    if (lastEntry) {
                        lastEntry.endDate = today;
                    }

                    const memberRef = doc(db, `artifacts/${getTenantId()}/public/data/members`, memberId);
                    batch.update(memberRef, {
                        connectId: '',
                        connectHistory: history
                    });
                }
            });

            // Adicionar connectId aos novos membros do Connect
            const membersToAdd = connectData.memberIds.filter(id => !currentMemberIds.includes(id));
            console.log('➕ Membros a adicionar:', membersToAdd.length);

            membersToAdd.forEach(memberId => {
                const member = allMembers.find(m => m.id === memberId);
                if (member) {
                    console.log('🔄 Adicionando membro:', member.name);
                    const history = member.connectHistory || [];
                    const lastEntry = history.find(entry => !entry.endDate);

                    // Finalizar entrada anterior se existir
                    if (lastEntry) {
                        lastEntry.endDate = today;
                    }

                    // Adicionar nova entrada
                    history.push({
                        connectId: connectId,
                        startDate: today,
                        endDate: null
                    });

                    const memberRef = doc(db, `artifacts/${getTenantId()}/public/data/members`, memberId);
                    batch.update(memberRef, {
                        connectId: connectId,
                        connectHistory: history
                    });
                }
            });

            // Garantir que o líder tenha o connectId correto
            console.log('👤 Atualizando Connect do líder...');
            const leaderRef = doc(db, `artifacts/${getTenantId()}/public/data/members`, connectData.leaderId);
            batch.update(leaderRef, { connectId: connectId });

            // Executar todas as atualizações em lote
            console.log('💾 Executando batch de atualizações...');
            await retryFirestoreOperation(async () => {
                await batch.commit();
            });
            console.log('✅ Batch executado com sucesso');

            loadingStates.setSuccess('saveConnect', 'Connect salvo com sucesso!');
            closeConnectModal();
        } catch (error) {
            console.error("âŒ Erro ao salvar connect:", error);
            console.error("📊 Stack trace:", error.stack);
            loadingStates.setError('saveConnect', `Erro ao salvar connect: ${error.message}`);
        }
    };
    const generateAttendanceRecords = async (courseId, courseData) => {
        const attendanceSnapshot = await getDocs(collection(db, `artifacts/${getTenantId()}/public/data/courses/${courseId}/attendance`));
        const existingRecords = {};
        attendanceSnapshot.forEach(d => { existingRecords[d.id] = d.data(); });

        const batch = writeBatch(db);
        const weekDaysMap = { "Domingo": 0, "Segunda-feira": 1, "Terça-feira": 2, "Quarta-feira": 3, "Quinta-feira": 4, "Sexta-feira": 5, "Sábado": 6 };
        const targetDay = courseData.isSporadic ? null : weekDaysMap[courseData.classDay];
        const activityPlanCsv = Array.isArray(courseData?.assessment?.activities?.plan) ? courseData.assessment.activities.plan : [];
        const lessonPlan = Array.isArray(courseData?.lessonPlan) ? courseData.lessonPlan : [];
        const hasLessonPlan = (courseData?.lessonsCount || 0) > 0 && lessonPlan.length > 0;
        let sessionNumber = 0;
        let currentDate = courseData.isSporadic ? null : new Date(courseData.startDate + 'T00:00:00');

        const createRecordForDate = (theDate) => {
            sessionNumber += 1;
            const dateString = theDate.toISOString().split('T')[0];
            const attendanceRef = doc(db, `artifacts/${getTenantId()}/public/data/courses/${courseId}/attendance/${dateString}`);
            
            const record = { date: theDate, sessionNumber };
            if (!existingRecords[dateString]) {
                const initialStatuses = (courseData.students || []).reduce((acc, student) => { acc[student.id] = 'pendente'; return acc; }, {});
                record.statuses = initialStatuses;
            }

            if (hasLessonPlan) {
                const planEntry = lessonPlan[sessionNumber - 1] || {};
                if (planEntry.activityIndex) record.activityIndex = planEntry.activityIndex; // 1-base
                if (planEntry.testIndex) record.testIndex = planEntry.testIndex; // 1-base
                if (planEntry.assignmentIndex) record.assignmentIndex = planEntry.assignmentIndex; // 1-base
                if (planEntry.notes) record.notes = planEntry.notes;
                if (planEntry.considerAttendance === false) record.ignoreAttendance = true;
            } else if (activityPlanCsv.length > 0) {
                const position = activityPlanCsv.indexOf(sessionNumber);
                if (position >= 0) {
                    // activityIndex é 1-base: A1 na primeira ocorrência do plano, A2 na segunda, etc.
                    record.activityIndex = position + 1;
                }
            }

            batch.set(attendanceRef, record, { merge: true });
        };

        if (courseData.isSporadic) {
            const sortedClasses = [...(courseData.sporadicClasses || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
            for (const sc of sortedClasses) {
                if (sc.date) {
                    const theDate = new Date(sc.date + 'T00:00:00');
                    createRecordForDate(theDate);
                }
            }
        } else {
            const lessonsCount = Number(courseData?.lessonsCount) || 0;
            if (lessonsCount > 0) {
                let generated = 0;
                while (generated < lessonsCount) {
                    if (currentDate.getUTCDay() === targetDay) {
                        createRecordForDate(currentDate);
                        generated += 1;
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            } else {
                const endDate = new Date(courseData.endDate + 'T00:00:00');
                while (currentDate <= endDate) {
                    if (currentDate.getUTCDay() === targetDay) {
                        createRecordForDate(currentDate);
                    }
                    currentDate.setDate(currentDate.getDate() + 1);
                }
            }
        }

        await batch.commit();
    };
    const handleSaveCourse = async (courseData) => {
        const collectionPath = `artifacts/${getTenantId()}/public/data/courses`;

        loadingStates.setLoading('saveCourse', 'Salvando curso...');

        try {
            if (editingCourse) {
                await setDoc(doc(db, collectionPath, editingCourse.id), courseData);
                await generateAttendanceRecords(editingCourse.id, courseData);
            } else {
                const newCourseRef = await addDoc(collection(db, collectionPath), courseData);
                await generateAttendanceRecords(newCourseRef.id, courseData);
            }

            loadingStates.setSuccess('saveCourse', 'Curso salvo com sucesso!');
            closeCourseModal();
        } catch (error) {
            console.error("Erro ao salvar curso:", error);
            loadingStates.setError('saveCourse', 'Erro ao salvar curso. Tente novamente.');
        }
    };

    const handleSaveCourseTemplate = async (templateData) => {
        const collectionPath = `artifacts/${getTenantId()}/public/data/courseTemplates`;
        try {
            const orderVal = typeof templateData.order === 'number' && !isNaN(templateData.order)
                ? templateData.order
                : (Number(templateData.order) || (allCourseTemplates.length + 1));
            const dataToSave = { ...templateData, order: orderVal };
            if (editingCourseTemplate) {
                await setDoc(doc(db, collectionPath, editingCourseTemplate.id), dataToSave, { merge: true });
            } else {
                await addDoc(collection(db, collectionPath), dataToSave);
            }
            closeCourseTemplateModal();
        } catch (error) {
            console.error("Erro ao salvar modelo de curso:", error);
        }
    };

    const handleReorderCourseTemplates = async (newTemplatesList) => {
        try {
            const batch = writeBatch(db);
            const collectionPath = `artifacts/${getTenantId()}/public/data/courseTemplates`;
            newTemplatesList.forEach((template, index) => {
                const templateRef = doc(db, collectionPath, template.id);
                batch.update(templateRef, { order: index + 1 });
            });
            await batch.commit();
        } catch (error) {
            console.error("Erro ao reordenar modelos de cursos:", error);
        }
    };
    const handleSaveCourseStudents = async (courseId, students) => {
        try {
            const course = allCourses.find(c => c.id === courseId);
            const userEmail = currentUserData?.email?.toLowerCase?.();
            const isSubActive = () => {
                try {
                    if (!course?.substituteTeacher?.teacherId || !Array.isArray(allMembers) || !userEmail) return false;
                    const substituteTeacher = allMembers.find(m => m.id === course.substituteTeacher.teacherId);
                    if (!substituteTeacher || substituteTeacher.email?.toLowerCase() !== userEmail) return false;
                    const today = new Date();
                    const startDate = new Date(course.substituteTeacher.startDate);
                    if (course.substituteTeacher.isIndefinite) return today >= startDate;
                    const endDate = new Date(course.substituteTeacher.endDate);
                    return today >= startDate && today <= endDate;
                } catch {
                    return false;
                }
            };
            const isAuxTeacher = () => {
                try {
                    const email = userEmail || '';
                    
                    // Auxiliar da turma inteira (legado e novo)
                    if (course?.auxTeacherId === currentUserData?.id || (course?.auxTeacherEmail || '').toLowerCase() === email) return true;
                    if (course?.auxiliaryTeacherId === currentUserData?.id || (course?.auxiliaryTeacherEmail || '').toLowerCase() === email) return true;
                    if (Array.isArray(course?.auxiliaryTeachers) && course.auxiliaryTeachers.some(a => a.id === currentUserData?.id || (a.email || '').toLowerCase() === email)) return true;

                    // Auxiliar de grupo
                    if (Array.isArray(course?.groups)) {
                        return course.groups.some(g => {
                            if (g.assistantId === currentUserData?.id || (g.assistantEmail || '').toLowerCase() === email) return true;
                            if (Array.isArray(g.assistants) && g.assistants.some(a => a.id === currentUserData?.id || (a.email || '').toLowerCase() === email)) return true;
                            return false;
                        });
                    }
                    return false;
                } catch {
                    return false;
                }
            };
            const allowed = isAdmin || (course && userEmail && (course.teacherEmail?.toLowerCase() === userEmail || isSubActive() || isAuxTeacher()));
            if (!allowed) {
                alert('Você não tem permissão para alterar a lista de alunos desta turma.');
                return;
            }
            const courseRef = doc(db, `artifacts/${getTenantId()}/public/data/courses/${courseId}`);
            await updateDoc(courseRef, { students: students });
        } catch (error) {
            console.error("Erro ao salvar alunos do curso:", error);
        }
    };
    const handleSaveAttendance = async (courseId, dateId, statuses, metaOrActivityIndex) => {
        try {
            const course = allCourses.find(c => c.id === courseId);
            const userEmail = currentUserData?.email?.toLowerCase?.();
            const isSubActive = () => {
                try {
                    if (!course?.substituteTeacher?.teacherId || !Array.isArray(allMembers) || !userEmail) return false;
                    const substituteTeacher = allMembers.find(m => m.id === course.substituteTeacher.teacherId);
                    if (!substituteTeacher || substituteTeacher.email?.toLowerCase() !== userEmail) return false;
                    const today = new Date();
                    const startDate = new Date(course.substituteTeacher.startDate);
                    if (course.substituteTeacher.isIndefinite) return today >= startDate;
                    const endDate = new Date(course.substituteTeacher.endDate);
                    return today >= startDate && today <= endDate;
                } catch {
                    return false;
                }
            };
            const isAuxTeacher = () => {
                try {
                    const email = userEmail || '';
                    
                    // Auxiliar da turma inteira (legado e novo)
                    if (course?.auxTeacherId === currentUserData?.id || (course?.auxTeacherEmail || '').toLowerCase() === email) return true;
                    if (course?.auxiliaryTeacherId === currentUserData?.id || (course?.auxiliaryTeacherEmail || '').toLowerCase() === email) return true;
                    if (Array.isArray(course?.auxiliaryTeachers) && course.auxiliaryTeachers.some(a => a.id === currentUserData?.id || (a.email || '').toLowerCase() === email)) return true;

                    // Auxiliar de grupo
                    if (Array.isArray(course?.groups)) {
                        return course.groups.some(g => {
                            if (g.assistantId === currentUserData?.id || (g.assistantEmail || '').toLowerCase() === email) return true;
                            if (Array.isArray(g.assistants) && g.assistants.some(a => a.id === currentUserData?.id || (a.email || '').toLowerCase() === email)) return true;
                            return false;
                        });
                    }
                    return false;
                } catch {
                    return false;
                }
            };
            const allowed = isAdmin || (course && userEmail && (course.teacherEmail?.toLowerCase() === userEmail || isSubActive() || isAuxTeacher()));
            if (!allowed) {
                alert('Você não tem permissão para registrar presença/notas nesta turma.');
                return;
            }
            const attendanceRef = doc(db, `artifacts/${getTenantId()}/public/data/courses/${courseId}/attendance/${dateId}`);
            const payload = {};
            if (statuses) payload.statuses = statuses;
            if (typeof metaOrActivityIndex !== 'undefined') {
                if (typeof metaOrActivityIndex === 'object' && metaOrActivityIndex !== null) {
                    const { activityIndex, ignoreAttendance, noClass, sessionNumber } = metaOrActivityIndex;
                    if (typeof activityIndex !== 'undefined') payload.activityIndex = activityIndex;
                    if (typeof ignoreAttendance !== 'undefined') payload.ignoreAttendance = !!ignoreAttendance;
                    if (typeof noClass !== 'undefined') payload.noClass = !!noClass;
                    if (typeof sessionNumber !== 'undefined') payload.sessionNumber = sessionNumber;
                } else {
                    payload.activityIndex = metaOrActivityIndex;
                }
            }
            await updateDoc(attendanceRef, payload);
        } catch (error) {
            console.error("Erro ao salvar presença:", error);
        }
    };
    const handleSkipClassDay = async (courseId, dateId) => {
        try {
            const course = allCourses.find(c => c.id === courseId);
            const userEmail = currentUserData?.email?.toLowerCase?.();
            const isSubActive = () => {
                try {
                    if (!course?.substituteTeacher?.teacherId || !Array.isArray(allMembers) || !userEmail) return false;
                    const substituteTeacher = allMembers.find(m => m.id === course.substituteTeacher.teacherId);
                    if (!substituteTeacher || substituteTeacher.email?.toLowerCase() !== userEmail) return false;
                    const today = new Date();
                    const startDate = new Date(course.substituteTeacher.startDate);
                    if (course.substituteTeacher.isIndefinite) return today >= startDate;
                    const endDate = new Date(course.substituteTeacher.endDate);
                    return today >= startDate && today <= endDate;
                } catch { return false; }
            };
            const allowed = isAdmin || (course && userEmail && (course.teacherEmail?.toLowerCase() === userEmail || isSubActive()));
            if (!allowed) { alert('Você não tem permissão para alterar o calendário desta turma.'); return; }

            const attendanceColl = collection(db, `artifacts/${getTenantId()}/public/data/courses/${courseId}/attendance`);
            const snapshot = await getDocs(query(attendanceColl, orderBy('date', 'asc')));
            const records = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            const targetIndex = records.findIndex(r => r.id === dateId);
            if (targetIndex < 0) return;

            // Marcar o dia como 'sem aula' e remover o número de sessão
            const batch = writeBatch(db);
            const targetRef = doc(db, `artifacts/${getTenantId()}/public/data/courses/${courseId}/attendance/${dateId}`);
            batch.update(targetRef, { noClass: true, sessionNumber: null });

            // Reatribuir números de sessão para os dias seguintes
            for (let i = targetIndex + 1; i < records.length; i++) {
                const rec = records[i];
                if (typeof rec.sessionNumber === 'number') {
                    const ref = doc(db, `artifacts/${getTenantId()}/public/data/courses/${courseId}/attendance/${rec.id}`);
                    batch.update(ref, { sessionNumber: rec.sessionNumber - 1 });
                }
            }

            // Adicionar uma semana ao calendário: criar novo registro após o último
            const last = records[records.length - 1];
            const lastDate = new Date(last.date.seconds ? last.date.seconds * 1000 : last.date);
            const nextDate = new Date(lastDate);
            nextDate.setDate(nextDate.getDate() + 7);
            const dateString = nextDate.toISOString().split('T')[0];
            const newRef = doc(db, `artifacts/${getTenantId()}/public/data/courses/${courseId}/attendance/${dateString}`);
            const initialStatuses = (course.students || []).reduce((acc, student) => { acc[student.id] = 'pendente'; return acc; }, {});

            // Próxima sessão é a maior sessão atual + 1
            const maxSession = records.reduce((m, r) => typeof r.sessionNumber === 'number' ? Math.max(m, r.sessionNumber) : m, 0);
            const nextSessionNumber = maxSession + 1;
            const record = { date: nextDate, statuses: initialStatuses, sessionNumber: nextSessionNumber };

            const lessonPlan = Array.isArray(course?.lessonPlan) ? course.lessonPlan : [];
            if ((course?.lessonsCount || 0) > 0 && lessonPlan.length > 0) {
                const planEntry = lessonPlan[nextSessionNumber - 1] || {};
                if (planEntry.activityIndex) record.activityIndex = planEntry.activityIndex;
                if (planEntry.testIndex) record.testIndex = planEntry.testIndex;
                if (planEntry.assignmentIndex) record.assignmentIndex = planEntry.assignmentIndex;
                if (planEntry.notes) record.notes = planEntry.notes;
                if (planEntry.considerAttendance === false) record.ignoreAttendance = true;
            }
            batch.set(newRef, record);

            await batch.commit();
        } catch (error) {
            console.error('Erro ao pular dia de aula:', error);
            alert('Não foi possível pular o dia de aula.');
        }
    };
    const handleSaveCourseGroups = async (courseId, groupsData) => {
        const docRef = doc(db, `artifacts/${getTenantId()}/public/data/courses`, courseId);
        loadingStates.setLoading('saveCourse', 'Salvando grupos...');
        try {
            await retryFirestoreOperation(async () => {
                await updateDoc(docRef, { groups: groupsData });
            });
            loadingStates.setSuccess('saveCourse', 'Grupos salvos com sucesso!');
        } catch (error) {
            console.error("Erro ao salvar grupos:", error);
            loadingStates.setError('saveCourse', 'Erro ao salvar grupos.');
        }
    };
    const handleSaveConnectReport = async (reportData) => {
        try {
            const dateObj = reportData.reportDate instanceof Date
                ? reportData.reportDate
                : (reportData.reportDate?.toDate ? reportData.reportDate.toDate() : new Date(reportData.reportDate));
            const dateString = formatLocalDateToISO(dateObj) || formatLocalDateToISO(new Date());
            const reportId = `${reportData.connectId}_${dateString}`;
            const reportRef = doc(db, `artifacts/${getTenantId()}/public/data/connect_reports`, reportId);

            loadingStates.setLoading('saveReport', 'Salvando relatório...');

            // Sanitiza todos os campos para garantir que nenhum undefined chegue ao Firestore
            const safeAttendance = {};
            if (reportData.attendance && typeof reportData.attendance === 'object') {
                Object.entries(reportData.attendance).forEach(([k, v]) => {
                    if (k && typeof v === 'string') safeAttendance[k] = v;
                });
            }

            const cleanPayload = {
                connectId: String(reportData.connectId || ''),
                connectName: String(reportData.connectName || ''),
                leaderId: String(reportData.leaderId || ''),
                leaderName: String(reportData.leaderName || ''),
                reportDate: dateObj,
                guests: Math.max(0, Number(reportData.guests) || 0),
                offering: Math.max(0, Number(reportData.offering) || 0),
                attendance: safeAttendance,
                updatedAt: new Date()
            };

            await retryFirestoreOperation(async () => {
                await setDoc(reportRef, cleanPayload, { merge: true });
            });

            // Atualização otimista no estado local para refletir imediatamente na UI
            setAllConnectReports(prev => {
                const list = Array.isArray(prev) ? prev : [];
                const idx = list.findIndex(r => r.id === reportId || (String(r.connectId) === cleanPayload.connectId && formatLocalDateToISO(r.reportDate) === dateString));
                const item = { id: reportId, ...cleanPayload };
                if (idx >= 0) {
                    const next = [...list];
                    next[idx] = { ...next[idx], ...item };
                    return next;
                }
                return [item, ...list];
            });

            loadingStates.setSuccess('saveReport', 'Relatório salvo com sucesso!');
            return { success: true, reportId };
        } catch (error) {
            console.error("Erro ao salvar relatório do Connect:", error);
            loadingStates.setError('saveReport', 'Erro ao salvar relatório. Tente novamente.');
            throw error;
        }
    };
    const handleSaveMilestones = async (memberId, milestones) => {
        if (!memberId) throw new Error("ID do membro não fornecido.");
        const memberRef = doc(db, `artifacts/${getTenantId()}/public/data/members`, memberId);

        // Salva exatamente o que foi passado — completed e date são independentes
        // date pode ser null se o usuário não preencheu — isso é válido
        const milestonesToSave = {};
        Object.keys(milestones || {}).forEach(key => {
            const m = milestones[key] || {};
            milestonesToSave[key] = {
                completed: Boolean(m.completed),
                date: m.date || null,
            };
        });

        await retryFirestoreOperation(async () => {
            await updateDoc(memberRef, { milestones: milestonesToSave });
        });

        // Atualização otimista no estado local para reflexo imediato sem re-fetch
        setAllMembers(prev => prev.map(m =>
            m.id === memberId ? { ...m, milestones: milestonesToSave } : m
        ));
        setViewingMember(prev =>
            prev && prev.id === memberId ? { ...prev, milestones: milestonesToSave } : prev
        );
        setEditingMember(prev =>
            prev && prev.id === memberId ? { ...prev, milestones: milestonesToSave } : prev
        );
    };
    const handleFinalizeCourse = async (course) => {
        if (!course || !course.id) return;
        const confirmation = window.confirm(`Tem certeza que deseja finalizar o curso "${course.name}" e processar os resultados? Esta ação não pode ser desfeita.`);
        if (!confirmation) return;
        try {
            const attendanceRef = collection(db, `artifacts/${getTenantId()}/public/data/courses/${course.id}/attendance`);
            const attendanceSnapshot = await getDocs(attendanceRef);
            const attendanceRecords = attendanceSnapshot.docs.map(d => d.data());
            let approvedCount = 0;
            const students = course.students || [];
            for (const student of students) {
                const status = getStudentStatusInfo(student, course, attendanceRecords);
                if (status === 'Aprovado') {
                    approvedCount++;
                    const finalGrade = calculateFinalGradeForStudent(student, course);
                    const completedCourseRef = doc(db, `artifacts/${getTenantId()}/public/data/members/${student.id}/completedCourses/${course.id}`);
                    await setDoc(completedCourseRef, {
                        courseName: course.name,
                        completionDate: new Date(),
                        finalGrade: finalGrade,
                        templateId: course.templateId || null,
                    });

                    // Se a turma pertencer a um modelo vinculado ao Trilho de Liderança, atualizar o marco do membro
                    if (course.templateId) {
                        const tmpl = allCourseTemplates.find(t => t.id === course.templateId);
                        if (tmpl?.linkedMilestoneId) {
                            try {
                                const memberRef = doc(db, `artifacts/${getTenantId()}/public/data/members`, student.id);
                                const memberSnap = await getDoc(memberRef);
                                if (memberSnap.exists()) {
                                    const curMilestones = memberSnap.data().milestones || {};
                                    const curMilestone = curMilestones[tmpl.linkedMilestoneId] || {};
                                    await updateDoc(memberRef, {
                                        [`milestones.${tmpl.linkedMilestoneId}.completed`]: true,
                                        [`milestones.${tmpl.linkedMilestoneId}.date`]: curMilestone.date || new Date().toISOString().split('T')[0]
                                    });
                                }
                            } catch (mErr) {
                                console.warn(`Erro ao atualizar marco vinculado do membro ${student.id}:`, mErr);
                            }
                        }
                    }
                }
            }
            const courseRef = doc(db, `artifacts/${getTenantId()}/public/data/courses`, course.id);
            await updateDoc(courseRef, { finalized: true });
            alert(`Curso "${course.name}" finalizado com sucesso! ${approvedCount} de ${students.length} alunos foram aprovados e tiveram seus registros atualizados.`);
        } catch (error) {
            console.error("Erro ao finalizar o curso:", error);
            alert("Ocorreu um erro ao processar os resultados do curso.");
        }
    };
    const handleReopenCourse = async (course) => {
        if (!course || !course.id) return;
        const confirmation = window.confirm(`Tem certeza que deseja reabrir o curso "${course.name}"? Isso permitirá novas edições de notas e presenças, mas não removerá os registros do Trilho de Liderança já criados.`);
        if (!confirmation) return;
        try {
            const courseRef = doc(db, `artifacts/${getTenantId()}/public/data/courses`, course.id);
            await updateDoc(courseRef, { finalized: false });
            alert(`Curso "${course.name}" foi reaberto com sucesso.`);
        } catch (error) {
            console.error("Erro ao reabrir o curso:", error);
            alert("Ocorreu um erro ao reabrir o curso.");
        }
    };
    const handleReactivateMember = async (member, connectId) => {
        if (!member || !connectId) return;
        const confirmation = window.confirm(`Tem certeza que deseja reativar ${member.name}? Isso registrará uma presença para este membro na data de hoje.`);
        if (!confirmation) return;
        try {
            const today = new Date();
            const dateString = formatLocalDateToISO(today);
            const reportId = `${connectId}_${dateString}`;
            const reportRef = doc(db, `artifacts/${getTenantId()}/public/data/connect_reports`, reportId);
            const reportSnap = await getDoc(reportRef);
            let attendanceData = {};
            if (reportSnap.exists()) {
                attendanceData = reportSnap.data().attendance || {};
            }
            attendanceData[member.id] = 'presente';
            const connect = allConnects.find(c => c.id === connectId);
            const reportData = {
                connectId: connectId,
                connectName: connect?.name || '',
                leaderId: connect?.leaderId || '',
                leaderName: connect?.leaderName || '',
                reportDate: today,
                guests: reportSnap.exists() ? reportSnap.data().guests || 0 : 0,
                offering: reportSnap.exists() ? reportSnap.data().offering || 0 : 0,
                attendance: attendanceData
            };
            await setDoc(reportRef, reportData, { merge: true });
            alert(`${member.name} foi reativado com sucesso.`);
        } catch (error) {
            console.error("Erro ao reativar membro:", error);
            alert("Ocorreu um erro ao tentar reativar o membro.");
        }
    };
    const handleConfirmDelete = async () => {
        if (!deleteAction) return;
        const { type, id } = deleteAction;

        loadingStates.setLoading('delete', 'Excluindo...');

        try {
            let collectionPath = `artifacts/${getTenantId()}/public/data/${type}s`;
            if (type === 'courseTemplate') {
                collectionPath = `artifacts/${getTenantId()}/public/data/courseTemplates`;
            } else if (type === 'certificateTemplate') {
                collectionPath = `artifacts/${getTenantId()}/public/data/certificate_templates`;
            }

            if (type === 'connect') {
                const batch = writeBatch(db);
                const membersToUpdateQuery = query(collection(db, `artifacts/${getTenantId()}/public/data/members`), where('connectId', '==', id));
                const membersSnapshot = await getDocs(membersToUpdateQuery);
                membersSnapshot.forEach(memberDoc => {
                    batch.update(memberDoc.ref, { connectId: '' });
                });
                const connectRef = doc(db, collectionPath, id);
                batch.delete(connectRef);
                await batch.commit();
            } else {
                await deleteDoc(doc(db, collectionPath, id));
            }

            loadingStates.setSuccess('delete', 'Item excluído com sucesso!');
        } catch (error) {
            console.error(`Erro ao deletar ${type}:`, error);
            loadingStates.setError('delete', 'Erro ao excluir item. Tente novamente.');
        } finally {
            setConfirmModalOpen(false);
            setDeleteAction(null);
        }
    };

    const handleUpdateDecisionStatus = async (decisionId, newStatus) => {
        try {
            const ref = doc(db, `artifacts/${getTenantId()}/public/data/decisions`, decisionId);
            const update = { status: newStatus };
            // Ao marcar como contatado, registra o timestamp para controle dos 7 dias
            if (newStatus === 'contatado') {
                update.contactedAt = serverTimestamp();
            }
            await updateDoc(ref, update);
        } catch (error) {
            console.error('Erro ao atualizar status da decisão:', error);
            setOperationStatus({ type: 'error', message: 'Erro ao atualizar status do formulário.' });
        }
    };

    const getConnectName = useCallback((connectId) => { if (!connectId) return 'Sem Connect'; const connect = allConnects.find(c => c.id === connectId); return connect ? `${connect.number} - ${connect.name}` : '...'; }, [allConnects]);



    // Acesso direto ao módulo Kids antes de verificar auth (rota pública independente)
    if (isKidsRoute) {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route path="/kids/*" element={<KidsApp />} />
                </Routes>
            </Suspense>
        );
    }

    if (isLoadingAuth) return <LoadingSpinner />;

    if (!user) {
        // Rotas públicas: Login (default) e Signup
        // O modulo /kids tem auth propria — sempre acessivel
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route path="/signup" element={<SignupPage />} />
                    <Route path="/nova-decisao" element={<DecisionFormPage />} />
                    <Route path="/kids/*" element={<KidsApp />} />
                    <Route path="*" element={<LoginPage />} />
                </Routes>
            </Suspense>
        );
    }


    // Exibe mensagem de erro de conexão se houver
    if (connectionError) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
                    <div className="text-red-600 mb-4">
                        <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-semibold text-gray-800 mb-4">Erro de Conexão</h2>
                    <p className="text-gray-600 mb-6">{connectionError}</p>
                    <div className="space-y-3">
                        <button
                            onClick={() => {
                                setConnectionError(null);
                                window.location.reload();
                            }}
                            className="w-full bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 transition-colors"
                        >
                            Tentar Novamente
                        </button>
                        <button
                            onClick={handleLogout}
                            className="w-full bg-gray-300 text-gray-700 py-2 px-4 rounded hover:bg-gray-400 transition-colors"
                        >
                            Fazer Logout
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Logado mas em rota Kids: renderiza KidsApp sem o layout principal
    if (isKidsRoute) {
        return (
            <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                    <Route path="/kids/*" element={<KidsApp />} />
                </Routes>
            </Suspense>
        );
    }

    return (
        <div className={`flex h-screen bg-gray-100`}>
            {!isKidsRoute && (
            <Sidebar
                isOpen={isSidebarOpen}
                setIsOpen={setIsSidebarOpen}
                allConnects={allConnects}
                allCourses={allCourses}
                allMembers={allMembers}
            />
            )}
            <div className="flex-1 flex flex-col overflow-hidden">
                {!isKidsRoute && (
                <Header
                    onLogout={handleLogout}
                    onMenuClick={() => setIsSidebarOpen(true)}
                />
                )}
                {/* Mensagens de loading states */}
                <div className="mx-4 md:mx-8 mt-4 space-y-2">
                    {Object.entries(loadingStates.states).map(([operation, state]) => (
                        <LoadingMessage
                            key={operation}
                            state={state.status}
                            message={state.message}
                        />
                    ))}
                </div>

                {/* Mensagem de status de operação (legacy) */}
                {operationStatus.message && (
                    <div className={`mx-4 md:mx-8 mt-4 p-3 rounded-md ${operationStatus.type === 'success'
                        ? 'bg-green-100 border border-green-400 text-green-700'
                        : 'bg-red-100 border border-red-400 text-red-700'
                        }`}>
                        <div className="flex items-center">
                            <div className="flex-shrink-0">
                                {operationStatus.type === 'success' ? (
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                )}
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium">{operationStatus.message}</p>
                            </div>
                            <div className="ml-auto pl-3">
                                <button
                                    onClick={() => setOperationStatus({ type: null, message: null })}
                                    className="inline-flex text-sm font-medium hover:opacity-75"
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                
                {impersonatedUser && !isKidsRoute && (
                    <div className="bg-amber-500 text-black px-4 py-2 flex justify-between items-center z-50">
                        <span className="font-bold">
                            Visualizando como: {impersonatedUser.name || impersonatedUser.email}
                        </span>
                        <button 
                            onClick={clearImpersonation}
                            className="bg-black bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm font-semibold transition-colors"
                        >
                            Voltar ao meu perfil
                        </button>
                    </div>
                )}
                <main className={`flex-1 overflow-y-auto ${isKidsRoute ? '' : 'p-4 md:p-8'}`}>
                    <AppRouter
                        // Props para dados
                        allMembers={allMembers}
                        allConnects={allConnects}
                        allCourses={allCourses}
                         allCourseTemplates={allCourseTemplates}
                         allCertificateTemplates={allCertificateTemplates}
                        allConnectReports={allConnectReports}
                        allDecisions={allDecisions}
                        allSimpleMembers={allSimpleMembers}
                        combinedMembers={combinedMembers}
                        visibleMembers={visibleMembers}
                        visibleConnects={visibleConnects}
                        visibleCourses={visibleCourses}
                        visibleReports={visibleReports}
                        attendanceAlerts={attendanceAlerts}
                        membersWithCourses={membersWithCourses}
                        completedCourses={completedCourses}
                        memberConnectHistoryDetails={memberConnectHistoryDetails}
                        loadingStates={loadingStates}
                        operationStatus={operationStatus}
                        setOperationStatus={setOperationStatus}

                        // Handlers para modais
                        handleAddMember={openMemberModal}
                        handleEditMember={openMemberModal}
                        handleDeleteMember={triggerDelete}
                        handleReactivateMember={handleReactivateMember}
                        handleAddConnect={openConnectModal}
                        handleEditConnect={openConnectModal}
                        handleDeleteConnect={triggerDelete}
                        handleAddCourse={openCourseModal}
                        handleEditCourse={openCourseModal}
                        handleDeleteCourse={triggerDelete}
                        handleManageCourse={openManageCourseModal}
                        handleAddCourseTemplate={openCourseTemplateModal}
                        handleEditCourseTemplate={openCourseTemplateModal}
                        handleReorderCourseTemplates={handleReorderCourseTemplates}
                        handleDeleteCourseTemplate={triggerDelete}
                        handleDeleteCertificateTemplate={triggerDelete}
                        handleGenerateReport={openReportModal}
                        handleGenerateFullReport={openConnectFullReportModal}
                        handleViewMember={openLeadershipTrackModal}
                        handleViewConnectTrack={openConnectTrackPage}
                        handleLeadershipTrack={openLeadershipTrackModal}
                        handleSetAuxLeader={handleSetAuxLeader}
                        handleRemoveAuxLeader={handleRemoveAuxLeader}
                        handleSetAuxTeacher={handleSetAuxTeacher}
                        handleFinalizeCourse={handleFinalizeCourse}
                        handleReopenCourse={handleReopenCourse}
                        handleUpdateDecisionStatus={handleUpdateDecisionStatus}
                        handleSaveCourseGroups={handleSaveCourseGroups}
                        handleSaveSimpleMember={handleSaveSimpleMember}
                        handleDeleteSimpleMember={handleDeleteSimpleMember}

                        // Funções de utilidade
                        calculateFinalGradeForStudent={calculateFinalGradeForStudent}
                        getStudentStatusInfo={getStudentStatusInfo}
                        areNamesSimilar={areNamesSimilar}
                        getConnectName={getConnectName}
                    />
                </main>
            </div>

            <Modal isOpen={isMemberModalOpen} onClose={closeMemberModal} size="2xl" title={editingMember ? 'Editar Membro' : 'Novo Membro'}>
                <MemberForm
                    onClose={closeMemberModal}
                    onSave={handleSaveMember}
                    connects={allConnects}
                    editingMember={editingMember}
                    isAdmin={isAdmin}
                    leaderConnects={visibleConnects}
                    onCheckDuplicate={handleCheckDuplicate}
                    onOpenLeadershipTrack={openLeadershipTrackModal}
                />
            </Modal>
            <Modal
                isOpen={isConnectModalOpen}
                onClose={closeConnectModal}
                title={editingConnect ? 'Editar Connect' : 'Novo Connect'}
            >
                <ConnectForm
                    onClose={closeConnectModal}
                    onSave={handleSaveConnect}
                    members={allMembers}
                    editingConnect={editingConnect}
                    connects={allConnects}
                />
            </Modal>
            <Modal isOpen={isCourseModalOpen} onClose={closeCourseModal} size="2xl" title={editingCourse ? 'Editar Turma' : 'Nova Turma'}>
                <CourseForm
                    onClose={closeCourseModal}
                    onSave={handleSaveCourse}
                    members={allMembers}
                    allCourseTemplates={allCourseTemplates}
                    editingCourse={editingCourse}
                />
            </Modal>
            <Modal isOpen={isCourseTemplateModalOpen} onClose={closeCourseTemplateModal} size="2xl" title={editingCourseTemplate ? 'Editar Modelo de Curso' : 'Novo Modelo de Curso'}>
                <CourseTemplateForm
                    onClose={closeCourseTemplateModal}
                    onSave={handleSaveCourseTemplate}
                    editingTemplate={editingCourseTemplate}
                    courseTemplatesCount={allCourseTemplates.length}
                />
            </Modal>
            {reportingConnect && <ConnectReportModal isOpen={isReportModalOpen} onClose={closeReportModal} connect={reportingConnect} members={allMembers} onSave={handleSaveConnectReport} isAdmin={isAdmin} onViewMember={openMemberDetails} allReports={allConnectReports} />}
            {managingCourse && <ManageCourseModal course={managingCourse} members={allMembers} allMembers={allMembers} allSimpleMembers={allSimpleMembers} allCertificateTemplates={allCertificateTemplates} onSaveSimpleMember={handleSaveSimpleMember} areNamesSimilar={areNamesSimilar} isOpen={isManageCourseModalOpen} onClose={closeManageCourseModal} onSaveStudents={handleSaveCourseStudents} onSaveAttendance={handleSaveAttendance} onSkipClassDay={handleSkipClassDay} onViewMember={openMemberDetails} />}
            <ConfirmationModal isOpen={isConfirmModalOpen} onClose={() => setConfirmModalOpen(false)} onConfirm={handleConfirmDelete} title="Confirmar Exclusão" message={deleteAction?.message} />
            {generatingReportForConnect && <ConnectFullReportModal isOpen={isConnectFullReportModalOpen} onClose={closeConnectFullReportModal} connect={generatingReportForConnect} allMembers={allMembers} allReports={allConnectReports} />}

            {viewingMember && <Modal isOpen={isLeadershipTrackModalOpen} onClose={closeLeadershipTrackModal} size="md" title="Trilho de Liderança">
                <IndividualTrackModal
                    member={viewingMember}
                    completedCourses={completedCourses}
                    memberConnectHistoryDetails={memberConnectHistoryDetails}
                    onBack={closeLeadershipTrackModal}
                    onSave={handleSaveMilestones}
                />
            </Modal>}

            {viewingMember && <Modal isOpen={isMemberDetailsOpen} onClose={closeMemberDetails} size="md" title="Cadastro do Membro">
                <MemberDetailsModal member={viewingMember} allConnects={allConnects} allMembers={allMembers} />
            </Modal>}



            <DuplicateMemberModal
                isOpen={isDuplicateModalOpen}
                onClose={() => setDuplicateModalOpen(false)}
                existingMember={existingMemberForCheck}
                newMemberData={newMemberForCheck}
                onConfirm={handleConfirmDuplicate}
                onReject={handleRejectDuplicate}
            />
        </div>
    );
}

export default function App() {
    return (
        <BrowserRouter>
            <AppContent />
        </BrowserRouter>
    );
}



